import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { isCoarsePointer } from "../../utils/isCoarsePointer";
import { styleTxn } from "../../utils/style-txn";

type CursorTargetState = {
  prevX: number;
  prevY: number;
};

const targetStates = new WeakMap<StringObject, CursorTargetState>();
const CURSOR_EPSILON = 0.005;

function getTargetState(object: StringObject): CursorTargetState {
  let state = targetStates.get(object);
  if (!state) {
    state = { prevX: Number.NaN, prevY: Number.NaN };
    targetStates.set(object, state);
  }
  return state;
}

/**
 * StringCursor Module
 *
 * Handles cursor tracking and hover states for StringTune objects.
 *
 * Safari Navigation Fix:
 * Safari has an issue where mouseleave events are not fired when navigation occurs
 * (especially with NuxtLink/router navigation). This module includes several
 * workarounds to ensure proper cleanup:
 *
 * 1. MutationObserver - detects when elements are removed from DOM
 * 2. beforeunload/pagehide - captures traditional navigation
 * 3. visibilitychange - captures modern SPA navigation
 * 4. DOM existence checks - prevents operations on removed elements
 */
export class StringCursor extends StringModule {
  protected enterObjectsMap: Map<string, StringObject> = new Map<string, StringObject>();
  protected enterObjects: Array<StringObject> = new Array<StringObject>();
  cursor: any;
  cursorContent: any;
  overCount: number = 0;
  private cursorPrev = { x: Number.NaN, y: Number.NaN, stepX: Number.NaN, stepY: Number.NaN };
  protected enabled = true;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "cursor";

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.cursor = document.querySelector("[string-cursor],[data-string-cursor]") as HTMLElement;
    this.cursorContent = document.querySelector(
      "[string-cursor-content],[data-string-cursor-content]"
    ) as HTMLElement;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "target-disable",
        type: "boolean",
        fallback: this.settings["target-disable"],
      },
      {
        key: "target-style-disable",
        type: "boolean",
        fallback: this.settings["target-style-disable"],
      },
      {
        key: "target-class",
        type: "string",
        fallback: this.settings["target-class"],
      },
      {
        key: "cursor-class",
        type: "string",
        fallback: this.settings["cursor-class"],
      },
      {
        key: "alignment",
        type: { type: "enum", values: ["start", "center", "end"] },
        fallback: this.settings["alignment"],
      },
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (value: number) => {
          return this.tools.adaptiveLerp.process({
            value,
            inMin: 0.1,
            inMax: 1.0,
            outMin: 0.05,
            outMax: 0.65,
          });
        },
      },
    ];

    if (isCoarsePointer()) {
      this.enabled = false;
    }
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("mouse-x", 0);
    object.setProperty("mouse-y", 0);
    object.setProperty("mouse-pixel-x", 0);
    object.setProperty("mouse-pixel-y", 0);
    object.setProperty("is-mouse-over", false);
    object.setProperty("is-mouse-move", false);
  }

  onMutate(data: StringData): void {
    if (!this.enabled) return;
    const cursorX = this.data.cursor.targetX;
    const cursorY = this.data.cursor.targetY;

    this.objects.forEach((object) => {
      const isOver = object.getProperty<boolean>("is-mouse-over");
      const isDisabled = object.getProperty<boolean>("cursor-target-disable");
      const lerpFactor = object.getProperty<number>("lerp") ?? 0.15;

      const halfWidth =
        object.getProperty<number>("half-width") ?? object.htmlElement.offsetWidth / 2;
      const halfHeight =
        object.getProperty<number>("half-height") ?? object.htmlElement.offsetHeight / 2;
      const width = halfWidth * 2 || object.htmlElement.offsetWidth || 1;
      const height = halfHeight * 2 || object.htmlElement.offsetHeight || 1;

      if (isOver && !isDisabled) {
        const rect = object.htmlElement.getBoundingClientRect();
        const { cx, cy } = this.centers.getCenter(object);
        const elementX = cursorX - (cx - halfWidth);
        const elementY = cursorY - (cy - halfHeight);

        // const cursorX = this.data.cursor.targetX;
        // const cursorY = this.data.cursor.targetY;
        // const elementX = cursorX - rect.left;
        // const elementY = cursorY - rect.top;

        let px = object.getProperty<number>("mouse-pixel-x") ?? 0;
        let py = object.getProperty<number>("mouse-pixel-y") ?? 0;

        const dx = px - elementX;
        const dy = py - elementY;
        const distSquared = dx * dx + dy * dy;

        if (distSquared > 0.0001) {
          const isMoving = object.getProperty<boolean>("is-mouse-move") ?? false;
          if (!isMoving) {
            object.setProperty("is-mouse-move", true);
            object.setProperty("mouse-pixel-x", elementX);
            object.setProperty("mouse-pixel-y", elementY);
            object.setProperty("mouse-x", elementX);
            object.setProperty("mouse-y", elementY);
            px = elementX;
            py = elementY;
            this.events.emit(`cursor:start:${object.id}`, null);
          }

          const lerpedX = this.tools.lerp.process({
            from: px,
            to: elementX,
            progress: lerpFactor,
          });
          const lerpedY = this.tools.lerp.process({
            from: py,
            to: elementY,
            progress: lerpFactor,
          });

          const updatedX = px + lerpedX;
          const updatedY = py + lerpedY;
          const pixelChanged =
            Math.abs(updatedX - px) > CURSOR_EPSILON || Math.abs(updatedY - py) > CURSOR_EPSILON;

          object.setProperty("mouse-pixel-x", updatedX);
          object.setProperty("mouse-pixel-y", updatedY);

          const alignment = object.getProperty<string>("alignment") ?? "center";
          const offsetX = this.calculateOffset(alignment, updatedX, width);
          const offsetY = this.calculateOffset(alignment, updatedY, height);

          object.setProperty("mouse-x", offsetX);
          object.setProperty("mouse-y", offsetY);

          const coordsChanged = this.setMouseCoordinates(object, offsetX, offsetY);

          if (coordsChanged) {
            this.events.emit(`cursor:move:${object.id}`, {
              x: offsetX,
              y: offsetY,
            });
          }
          if (pixelChanged) {
            this.events.emit(`cursor:pixel:${object.id}`, {
              x: updatedX,
              y: updatedY,
            });
          }
        } else {
          object.setProperty("mouse-pixel-x", elementX);
          object.setProperty("mouse-pixel-y", elementY);
          if (object.getProperty<boolean>("is-mouse-move")) {
            object.setProperty("is-mouse-move", false);
            this.events.emit(`cursor:end:${object.id}`, null);
          }
          this.setMouseCoordinates(object, 0, 0);
        }
      } else {
        const mouseX = object.getProperty<number>("mouse-x") ?? 0;
        const mouseY = object.getProperty<number>("mouse-y") ?? 0;
        if (mouseX !== 0 || mouseY !== 0) {
          object.setProperty("is-mouse-move", false);

          const targetX = this.calculateOffset("center", halfWidth, width);
          const targetY = this.calculateOffset("center", halfHeight, height);

          const newMouseX =
            mouseX +
            this.tools.lerp.process({
              from: mouseX,
              to: targetX,
              progress: lerpFactor,
            });
          const newMouseY =
            mouseY +
            this.tools.lerp.process({
              from: mouseY,
              to: targetY,
              progress: lerpFactor,
            });

          object.setProperty("mouse-x", newMouseX);
          object.setProperty("mouse-y", newMouseY);

          if (Math.abs(newMouseX) < 0.001 && Math.abs(newMouseY) < 0.001) {
            object.setProperty("mouse-x", 0);
            object.setProperty("mouse-y", 0);
            object.setProperty("mouse-pixel-x", 0);
            object.setProperty("mouse-pixel-y", 0);
            this.setMouseCoordinates(object, 0, 0);
          } else {
            this.setMouseCoordinates(object, newMouseX, newMouseY);
          }
        }
      }
    });

    if (this.cursor) {
      const { stepX, stepY, smoothedX, smoothedY } = this.data.cursor;
      const prev = this.cursorPrev;
      const cursorChanged =
        !Number.isFinite(prev.x) ||
        Math.abs(smoothedX - prev.x) > CURSOR_EPSILON ||
        Math.abs(smoothedY - prev.y) > CURSOR_EPSILON ||
        Math.abs(stepX - prev.stepX) > CURSOR_EPSILON ||
        Math.abs(stepY - prev.stepY) > CURSOR_EPSILON;

      if (cursorChanged) {
        this.events.emit("cursor", {
          stepX,
          stepY,
          x: smoothedX,
          y: smoothedY,
        });

        this.cursor.style.setProperty("--x", smoothedX.toString());
        this.cursor.style.setProperty("--y", smoothedY.toString());
        this.cursor.style.setProperty("--x-lerp", stepX.toString());
        this.cursor.style.setProperty("--y-lerp", stepY.toString());

        this.cursorPrev = { x: smoothedX, y: smoothedY, stepX, stepY };
      }
    }
  }

  onObjectConnected(object: StringObject) {
    const element = object.htmlElement;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    this.centers.attach(object);
    object.setProperty("timeoutId", timeoutId);

    object.setProperty("mouseleave", () => {
      this.onMouseLeave(object);
    });
    object.setProperty("mouseenter", () => {
      this.onMouseEnter(object);
    });

    const beforeunloadHandler = () => {
      this.safariNavigationCleanup(object);
    };
    const pagehideHandler = () => {
      this.safariNavigationCleanup(object);
    };

    object.setProperty("beforeunload", beforeunloadHandler);
    object.setProperty("pagehide", pagehideHandler);

    const visibilityChangeHandler = () => {
      if (document.hidden && object.getProperty<boolean>("is-mouse-over")) {
        this.safariNavigationCleanup(object);
      }
    };
    object.setProperty("visibilitychange", visibilityChangeHandler);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach((node) => {
            if (node === element || (node instanceof Element && node.contains(element))) {
              this.onElementRemovedFromDOM(object);
            }
          });
        }
      });
    });

    if (element.parentNode) {
      mutationObserver.observe(element.parentNode, {
        childList: true,
        subtree: true,
      });
    }

    object.setProperty("mutationObserver", mutationObserver);

    object.setProperty("onEnterEvent", this.onEnterObject.bind(this));
    object.events.on("enter", object.getProperty<(object: StringObject) => void>("onEnterEvent"));
    object.setProperty("onLeaveEvent", this.onLeaveObject.bind(this));
    object.events.on("leave", object.getProperty<(object: StringObject) => void>("onLeaveEvent"));
  }

  getCursorClass(object: StringObject) {
    const value = object.getProperty<string>("cursor-class");
    return value != null && value.length > 0 ? value : null;
  }

  onMouseEnter(object: StringObject) {
    if (!document.contains(object.htmlElement)) {
      return;
    }

    this.overCount++;
    object.setProperty("is-mouse-over", true);

    const cursorClass = this.getCursorClass(object);

    if (this.cursor) {
      if (cursorClass) {
        this.cursor.classList.add(cursorClass);
      }
      this.cursor.classList.add("-showing");

      object.setProperty(
        "timeoutId",
        setTimeout(() => {
          if (!document.contains(object.htmlElement)) {
            return;
          }
          this.cursor.classList.remove("-showing");
          this.cursor.classList.add("-show");
        }, 1200)
      );
    }

    object.htmlElement.addEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  onMouseLeave(object: StringObject) {
    this.overCount--;
    object.setProperty("is-mouse-over", false);

    if (object.getProperty("timeoutId")) {
      clearTimeout(object.getProperty("timeoutId"));
      object.setProperty("timeoutId", null);
    }

    const cursorClass = this.getCursorClass(object);
    if (this.cursor) {
      if (cursorClass) {
        this.cursor.classList.remove(cursorClass);
      }

      this.cursor.classList.remove("-showing");
      this.cursor.classList.remove("-show");
    }

    // Safari Fix: Check if element is still in DOM before removing listeners
    if (document.contains(object.htmlElement)) {
      object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
    }
  }

  private onEnterObject(object: StringObject) {
    object.htmlElement.addEventListener("mouseenter", object.getProperty("mouseenter"));

    window.addEventListener("beforeunload", object.getProperty("beforeunload"));
    window.addEventListener("pagehide", object.getProperty("pagehide"));
    document.addEventListener("visibilitychange", object.getProperty("visibilitychange"));
  }

  private onLeaveObject(object: StringObject) {
    object.htmlElement.removeEventListener("mouseenter", object.getProperty("mouseenter"));
    object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));

    const beforeunload = object.getProperty<() => void>("beforeunload");
    const pagehide = object.getProperty<() => void>("pagehide");
    const visibilitychange = object.getProperty<() => void>("visibilitychange");

    if (beforeunload) {
      window.removeEventListener("beforeunload", beforeunload);
    }
    if (pagehide) {
      window.removeEventListener("pagehide", pagehide);
    }
    if (visibilitychange) {
      document.removeEventListener("visibilitychange", visibilitychange);
    }

    const mutationObserver = object.getProperty<MutationObserver>("mutationObserver");
    if (mutationObserver) {
      mutationObserver.disconnect();
    }
  }

  private safariNavigationCleanup(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  private onElementRemovedFromDOM(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  onObjectDisconnected(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }

    const beforeunload = object.getProperty<() => void>("beforeunload");
    const pagehide = object.getProperty<() => void>("pagehide");
    const visibilitychange = object.getProperty<() => void>("visibilitychange");

    if (beforeunload) {
      window.removeEventListener("beforeunload", beforeunload);
    }
    if (pagehide) {
      window.removeEventListener("pagehide", pagehide);
    }
    if (visibilitychange) {
      document.removeEventListener("visibilitychange", visibilitychange);
    }

    const mutationObserver = object.getProperty<MutationObserver>("mutationObserver");
    if (mutationObserver) {
      mutationObserver.disconnect();
    }
  }

  private setMouseCoordinates(object: StringObject, x: number, y: number): boolean {
    if (object.getProperty("cursor-target-style-disable")) {
      return false;
    }

    const state = getTargetState(object);
    const roundedX = Math.abs(x) < CURSOR_EPSILON ? state.prevX : Math.round(x * 100) / 100;
    const roundedY = Math.abs(y) < CURSOR_EPSILON ? state.prevY : Math.round(y * 100) / 100;

    if (
      Number.isFinite(state.prevX) &&
      Math.abs(roundedX - state.prevX) <= CURSOR_EPSILON &&
      Number.isFinite(state.prevY) &&
      Math.abs(roundedY - state.prevY) <= CURSOR_EPSILON
    ) {
      return false;
    }

    state.prevX = roundedX;
    state.prevY = roundedY;

    const valueX = roundedX.toFixed(2);
    const valueY = roundedY.toFixed(2);

    this.applyToElementAndConnects(object, (el) => {
      styleTxn.setVars(el, { "--x": valueX, "--y": valueY });
    });

    return true;
  }

  private calculateOffset(alignment: string, mousePos: number, size: number): number {
    switch (alignment) {
      case "start":
        return mousePos / size;
      case "end":
        return (mousePos - size) / size;
      case "center":
      default:
        return (mousePos - size / 2) / (size / 2);
    }
  }

  override removeObject(id: string): void {
    if (!this.enabled) return super.removeObject(id);
    const obj = this.objectMapOnPage.get(id);
    if (obj) {
      this.centers.detach(obj);
    }
    super.removeObject(id);
  }
}
